import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import {
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { ChatRecord } from "@/shared/domain/chat";

import styles from "./adminTable.module.css";

interface ChatsTableProps {
  chats: ChatRecord[];
  isLoading: boolean;
  selectedChatId: string | undefined;
  onSelectChat: (chatId: string) => void;
  feedbackCountsByChat?: Record<
    string,
    { positive: number; negative: number } | undefined
  >;
  onOpenFeedbackForChat?: (chatId: string) => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  currentPage: number;
}

export function ChatsTable({
  chats,
  isLoading,
  selectedChatId,
  onSelectChat,
  feedbackCountsByChat,
  onOpenFeedbackForChat,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  currentPage,
}: ChatsTableProps) {
  const theme = useTheme();

  if (isLoading && chats.length === 0) {
    return (
      <div className={styles.emptyState}>
        <CircularProgress size="1.5rem" />
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div className={styles.emptyState}>
        {hasPreviousPage
          ? "No chats on this page. Use Previous to go back."
          : "No chats found."}
      </div>
    );
  }

  return (
    <div>
      <TableContainer className={styles.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Session ID</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Org ID</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Feedback</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {chats.map((chat) => {
              const isSelected = chat.session_id === selectedChatId;
              const counts = feedbackCountsByChat?.[chat.session_id];
              return (
                <TableRow
                  key={chat.session_id}
                  onClick={() => onSelectChat(chat.session_id)}
                  className={`${styles.clickableRow} ${isSelected ? styles.selectedRow : ""}`}
                >
                  <TableCell className={styles.cellTruncate}>
                    {chat.title || "Untitled"}
                  </TableCell>
                  <TableCell className={styles.cellTruncate}>
                    {chat.session_id}
                  </TableCell>
                  <TableCell className={styles.cellTruncate}>
                    {chat.created_by}
                  </TableCell>
                  <TableCell className={styles.cellTruncate}>
                    {chat.org_id}
                  </TableCell>
                  <TableCell>
                    {new Date(chat.created).toLocaleString()}
                  </TableCell>
                  <TableCell>{chat.model_profile || "-"}</TableCell>
                  <TableCell>
                    {counts ? (
                      <span
                        role={onOpenFeedbackForChat ? "button" : undefined}
                        tabIndex={onOpenFeedbackForChat ? 0 : undefined}
                        className={styles.feedbackCounts}
                        onClick={(e) => {
                          if (!onOpenFeedbackForChat) {
                            return;
                          }
                          e.stopPropagation();
                          onOpenFeedbackForChat(chat.session_id);
                        }}
                        onKeyDown={(e) => {
                          if (!onOpenFeedbackForChat) {
                            return;
                          }
                          if (e.key === "Enter" || e.key === " ") {
                            // ``preventDefault`` so Space doesn't also
                            // scroll the page (the default behaviour for
                            // Space on a focusable element).
                            e.preventDefault();
                            e.stopPropagation();
                            onOpenFeedbackForChat(chat.session_id);
                          }
                        }}
                      >
                        <ThumbUpAltIcon
                          fontSize="inherit"
                          sx={{ color: theme.palette.success.main }}
                        />{" "}
                        {counts.positive}{" "}
                        <ThumbDownAltIcon
                          fontSize="inherit"
                          sx={{ color: theme.palette.error.main }}
                        />{" "}
                        {counts.negative}
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={styles.paginationContainer}>
        <span className={styles.pageIndicator}>Page {currentPage + 1}</span>
        <div className={styles.paginationButtons}>
          <IconButton
            size="small"
            onClick={onPreviousPage}
            disabled={!hasPreviousPage || isLoading}
            aria-label="Previous page"
          >
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={onNextPage}
            disabled={!hasNextPage || isLoading}
            aria-label="Next page"
          >
            <NavigateNextIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
